// Maps a completed bill + tenant + location into the flat `Receipt*Data` shapes
// the ESC/POS builder and thermal printer consume.
//
// Previously this lived twice: once as `getReceiptData()` in BillDetailPage and
// again inline in BillingPage's auto-print block. The two had drifted — only
// BillDetailPage passed tax-presentation options to `printReceipt`, so a GCC
// tenant printing from the billing page silently got India/GST labels. Sharing
// one mapper keeps them honest.

import type {
    ReceiptBillData,
    ReceiptTenantData,
    ReceiptLocationData,
} from '@/lib/escpos';

/** Bill fields the receipt needs. Structural so both `BillWithItems` and the
 *  store-shaped objects satisfy it without importing either concrete type. */
export interface ReceiptSourceBill {
    id: string;
    bill_number: string;
    status: string;
    created_at: string;
    subtotal: number;
    discount_total: number;
    tax_total: number;
    grand_total: number;
    rounding_adjustment?: number | null;
    payment_method?: string | null;
    amount_paid: number;
    change_due: number;
    payments?: Array<{ payment_method: string; amount: number }>;
    customer_id?: string | null;
    customer_name?: string | null;
    customer_phone?: string | null;
    notes?: string | null;
    return_against?: string | null;
    original_bill_number?: string | null;
    redemption_code?: string | null;
    credit_note_verify_url?: string | null;
    /** T19: the till's OFL number when this bill was sold offline and has synced. */
    offline_bill_number?: string | null;
    /** India GST: the bill's stamped tax type (gst-parked 080). */
    supply_type?: 'INTRA' | 'INTER' | 'NONE' | null;
    items: Array<{
        product_name: string;
        product_sku: string;
        hsn_code?: string;
        quantity: number;
        unit?: string;
        unit_price: number;
        gst_rate: number;
        line_discount: number;
        line_tax: number;
        line_total: number;
    }>;
}

export interface ReceiptSourceTenant {
    business_name: string;
    gstin?: string | null;
    phone?: string | null;
    receipt_footer?: string | null;
    /** The owner's "Show VATIN/GSTIN on receipt" toggle; absent = shown. */
    show_gstin_on_receipt?: boolean;
}

export interface ReceiptSourceLocation {
    name: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    pincode?: string | null;
    phone?: string | null;
}

export interface ReceiptDataBundle {
    billData: ReceiptBillData;
    tenantData: ReceiptTenantData;
    locationData: ReceiptLocationData;
}

/**
 * Build the printer payload. Returns null when any input is missing so callers
 * can fall back to the server-rendered PDF rather than printing a blank header.
 */
export function buildReceiptData(
    bill: ReceiptSourceBill | null | undefined,
    tenant: ReceiptSourceTenant | null | undefined,
    location: ReceiptSourceLocation | null | undefined,
): ReceiptDataBundle | null {
    if (!bill || !tenant || !location) return null;

    const billData: ReceiptBillData = {
        id: bill.id,
        bill_number: bill.bill_number,
        status: bill.status,
        created_at: bill.created_at,
        subtotal: bill.subtotal,
        discount_total: bill.discount_total,
        tax_total: bill.tax_total,
        grand_total: bill.grand_total,
        rounding_adjustment: Number(bill.rounding_adjustment ?? 0),
        payment_method: bill.payment_method ?? null,
        amount_paid: bill.amount_paid,
        change_due: bill.change_due,
        payments: bill.payments?.map((p) => ({ payment_method: p.payment_method, amount: Number(p.amount) })),
        customer_id: bill.customer_id ?? null,
        customer_name: bill.customer_name ?? null,
        customer_phone: bill.customer_phone ?? null,
        notes: bill.notes ?? null,
        return_against: bill.return_against ?? null,
        original_bill_number: bill.original_bill_number ?? null,
        redemption_code: bill.redemption_code ?? null,
        credit_note_verify_url: bill.credit_note_verify_url ?? null,
        offline_ref: bill.offline_bill_number ?? null,
        supply_type: bill.supply_type ?? null,
        items: bill.items.map((item) => ({
            product_name: item.product_name,
            product_sku: item.product_sku,
            hsn_code: item.hsn_code,
            quantity: item.quantity,
            unit: item.unit ?? 'pcs',
            unit_price: item.unit_price,
            gst_rate: item.gst_rate,
            line_discount: item.line_discount,
            line_tax: item.line_tax,
            line_total: item.line_total,
        })),
    };

    const tenantData: ReceiptTenantData = {
        business_name: tenant.business_name,
        gstin: tenant.show_gstin_on_receipt === false ? null : tenant.gstin ?? null,
        phone: tenant.phone ?? null,
        receipt_footer: tenant.receipt_footer ?? null,
    };

    const locationData: ReceiptLocationData = {
        name: location.name,
        address: location.address ?? null,
        city: location.city ?? null,
        state: location.state ?? null,
        pincode: location.pincode ?? null,
        phone: location.phone ?? null,
    };

    return { billData, tenantData, locationData };
}
